import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, catchError, map, Observable, of, tap } from 'rxjs';
import { AuthenticationApiService } from '../api-services/authentication-api.service';
import { AuthenticationService } from './authentication.service';
import { AuthProvider, ExternalAuthRequest, ExternalAuthResponse } from '../models/auth-provider';
import { FeatureToggleService } from './feature-toggle.service';

/**
 * Service to manage external authentication providers (OAuth/OIDC/SAML).
 *
 * Extensibility:
 * - New providers are added via the AuthProviders DB table (see migration 003).
 * - No code changes required to surface a new provider on the login page.
 * - Provider-specific logic (e.g., PKCE, nonce validation) is handled by
 *   configuration fields on AuthProvider (authority, endpoints, scopes).
 *
 * Future provider types:
 * - Universities: Configure as 'oidc' type with their own authority/endpoints
 * - Home Affairs: Configure as 'identity-verification' type
 * - SAML IDPs: Configure as 'saml' type
 */
@Injectable({
  providedIn: 'root',
})
export class AuthProviderService {
  private readonly authApi = inject(AuthenticationApiService);
  private readonly authService = inject(AuthenticationService);
  private readonly featureToggle = inject(FeatureToggleService);

  private providersSubject = new BehaviorSubject<AuthProvider[]>([]);
  public providers$ = this.providersSubject.asObservable();

  private isExternalAuthEnabled$: Observable<boolean> | null = null;

  /**
   * Check whether external auth providers are globally enabled.
   */
  isEnabled(): Observable<boolean> {
    if (!this.isExternalAuthEnabled$) {
      this.isExternalAuthEnabled$ = this.featureToggle.isEnabled('ExternalAuthProviders');
    }
    return this.isExternalAuthEnabled$;
  }

  /**
   * Load enabled auth providers from the backend.
   * Cached in BehaviorSubject for the lifetime of the app.
   */
  loadProviders(): Observable<AuthProvider[]> {
    return this.authApi.getAuthProviders().pipe(
      map((response: any) => {
        // Unwrap ApiResponse<T> wrapper: { success, data, message, statusCode }
        if (Array.isArray(response)) return response;
        if (response?.data && Array.isArray(response.data)) return response.data;
        return [];
      }),
      tap((providers) => this.providersSubject.next(providers)),
      catchError(() => {
        // Graceful degradation: no external providers on error
        this.providersSubject.next([]);
        return of([]);
      }),
    );
  }

  /**
   * Get the currently cached providers without a network call.
   */
  getCachedProviders(): AuthProvider[] {
    return this.providersSubject.value;
  }

  /**
   * Initiate the OAuth authorization code flow for a given provider.
   *
   * 1. Generates PKCE code_verifier + code_challenge (S256)
   * 2. Builds the authorization URL
   * 3. Redirects the user to the provider's consent screen
   *
   * After the user returns with an authorization code, call
   * `handleRedirectCallback()` to exchange it for tokens.
   */
  async initiateOAuthFlow(provider: AuthProvider, redirectUri: string): Promise<void> {
    if (!provider.clientId || !provider.authorizationEndpoint) {
      console.error(`Auth provider '${provider.providerKey}' is not fully configured.`);
      return;
    }

    // Generate PKCE code verifier and challenge
    const codeVerifier = this.generateCodeVerifier();
    const codeChallenge = await this.generateCodeChallenge(codeVerifier);

    // Store code_verifier for later exchange
    sessionStorage.setItem(`pkce_${provider.providerKey}`, codeVerifier);

    // Build authorization URL
    const params = new URLSearchParams({
      client_id: provider.clientId,
      redirect_uri: redirectUri,
      response_type: 'code',
      scope: provider.scopes || 'openid profile email',
      state: this.generateState(provider.providerKey),
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
    });

    const authUrl = `${provider.authorizationEndpoint}?${params.toString()}`;
    window.location.href = authUrl;
  }

  /**
   * Handle the OAuth redirect callback.
   * Exchanges the authorization code for application tokens.
   *
   * @param providerKey The provider key (e.g., 'google', 'microsoft')
   * @param code The authorization code from the query string
   * @param redirectUri The same redirect URI used in the authorization request
   */
  handleRedirectCallback(
    providerKey: string,
    code: string,
    redirectUri: string,
  ): Observable<ExternalAuthResponse> {
    const codeVerifier = sessionStorage.getItem(`pkce_${providerKey}`) || undefined;
    sessionStorage.removeItem(`pkce_${providerKey}`);

    const request: ExternalAuthRequest = {
      providerKey,
      authorizationCode: code,
      redirectUri,
      codeVerifier,
    };

    return this.authApi.externalLogin(request).pipe(
      tap((response) => {
        // Store session using the public method on AuthenticationService
        this.authService.completeExternalLogin({
          accessToken: response.accessToken,
          refreshToken: response.refreshToken,
          user: response.user,
        });
      }),
    );
  }

  /**
   * Link an external provider to the currently logged-in account.
   * Redirects the user to the provider, then exchanges the code.
   */
  async linkProviderToAccount(provider: AuthProvider, redirectUri: string): Promise<void> {
    await this.initiateOAuthFlow(provider, redirectUri);
    // The redirect callback will be handled by a dedicated page/component
    // that calls linkExternalProvider on the API
  }

  // ==========================================================================
  // PKCE Helpers (Proof Key for Code Exchange) — RFC 7636
  // ==========================================================================

  private generateCodeVerifier(): string {
    const array = new Uint8Array(64);
    crypto.getRandomValues(array);
    return this.base64UrlEncode(array);
  }

  private async generateCodeChallenge(verifier: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    return this.base64UrlEncode(new Uint8Array(hashBuffer));
  }

  private generateState(providerKey: string): string {
    const state = `${providerKey}_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
    sessionStorage.setItem('oauth_state', state);
    return state;
  }

  validateState(returnedState: string): boolean {
    const storedState = sessionStorage.getItem('oauth_state');
    sessionStorage.removeItem('oauth_state');
    return storedState === returnedState;
  }

  private base64UrlEncode(buffer: Uint8Array): string {
    let binary = '';
    for (let i = 0; i < buffer.length; i++) {
      binary += String.fromCharCode(buffer[i]);
    }
    return btoa(binary)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  }
}
